/**
 * Camera photoplethysmography (PPG) signal analysis.
 *
 * When a fingertip covers the camera lens (ideally with the torch on), each
 * heartbeat changes blood volume in the capillaries, which modulates how much
 * light is absorbed. The green channel carries the strongest signal
 * (haemoglobin absorbs green light well), so we track its frame mean over
 * time, detrend it, and detect pulse peaks to estimate heart rate.
 */

export interface PpgSample {
  /** Timestamp (performance.now(), ms). */
  t: number;
  /** Frame mean of the green channel. */
  v: number;
}

export interface PpgResult {
  bpm: number;
  quality: 'good' | 'fair';
  peaks: number;
}

/** Average red and green channel values of a frame's RGBA pixel data. */
export function channelMeans(data: Uint8ClampedArray): { red: number; green: number } {
  let red = 0;
  let green = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    red += data[i];
    green += data[i + 1];
  }
  return { red: red / pixels, green: green / pixels };
}

/** Heuristic: a fingertip fully covering the lens reads strongly red. */
export function fingerDetected(redMean: number): boolean {
  return redMean > 90;
}

/**
 * Estimate heart rate from a series of green-channel samples.
 * Returns null when the signal is too short or too noisy to trust.
 */
export function analyzePpg(samples: PpgSample[], samplesPerSecond = 30): PpgResult | null {
  if (samples.length < 120) return null; // need several seconds of signal

  const values = samples.map((s) => s.v);
  const times = samples.map((s) => s.t);

  // Detrend: subtract a moving average (~1s window) to remove slow drift
  // from finger pressure and ambient light changes.
  const window = Math.max(5, Math.round(samplesPerSecond));
  const detrended = values.map((v, i) => {
    const start = Math.max(0, i - window);
    const end = Math.min(values.length, i + window + 1);
    let sum = 0;
    for (let j = start; j < end; j++) sum += values[j];
    return v - sum / (end - start);
  });

  // Light 3-point smoothing to suppress frame noise.
  const smooth = detrended.map((v, i) => {
    const prev = detrended[Math.max(0, i - 1)];
    const next = detrended[Math.min(detrended.length - 1, i + 1)];
    return (prev + v + next) / 3;
  });

  // Adaptive threshold above the signal's baseline.
  const mean = smooth.reduce((a, b) => a + b, 0) / smooth.length;
  const std = Math.sqrt(smooth.reduce((a, b) => a + (b - mean) ** 2, 0) / smooth.length);
  const threshold = mean + 0.6 * std;

  // Peak detection with a 350 ms refractory period (max ~170 bpm).
  const peaks: number[] = [];
  for (let i = 1; i < smooth.length - 1; i++) {
    if (smooth[i] > threshold && smooth[i] >= smooth[i - 1] && smooth[i] > smooth[i + 1]) {
      const t = times[i];
      if (peaks.length === 0 || t - peaks[peaks.length - 1] > 350) {
        peaks.push(t);
      }
    }
  }
  if (peaks.length < 4) return null;

  // Inter-beat intervals, filtered to a physiologically plausible 40–180 bpm.
  const ibis: number[] = [];
  for (let i = 1; i < peaks.length; i++) {
    const ibi = peaks[i] - peaks[i - 1];
    if (ibi >= 330 && ibi <= 1500) ibis.push(ibi);
  }
  if (ibis.length < 3) return null;

  ibis.sort((a, b) => a - b);
  const medianIbi = ibis[Math.floor(ibis.length / 2)];
  const bpm = Math.round(60_000 / medianIbi);
  if (bpm < 40 || bpm > 180) return null;

  // Quality from beat-to-beat regularity (lower variability = cleaner signal).
  const ibiMean = ibis.reduce((a, b) => a + b, 0) / ibis.length;
  const ibiStd = Math.sqrt(ibis.reduce((a, b) => a + (b - ibiMean) ** 2, 0) / ibis.length);
  const variability = ibiStd / ibiMean;
  const quality = variability < 0.12 && peaks.length >= 7 ? 'good' : 'fair';

  return { bpm, quality, peaks: peaks.length };
}
